import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { InfoTooltip } from '../components/InfoTooltip';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Edit Protection Types
interface EditProtectionContextType {
  canEdit: boolean;
  isUnlocked: boolean;
  requestEdit: (
    onConfirm: () => Promise<void>,
    ruleInfo: { ruleType: string; ruleName: string; ruleId?: string; fieldChanged?: string; oldValue: string; newValue: string; action: string }
  ) => void;
}

const EditProtectionContext = createContext<EditProtectionContextType>({
  canEdit: false,
  isUnlocked: false,
  requestEdit: () => {},
});

const useEditProtection = () => useContext(EditProtectionContext);

// Edit Confirmation Modal - Multi-step confirmation process
function EditConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  ruleInfo,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  ruleInfo: { ruleType: string; ruleName: string; action: string } | null;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setConfirmText('');
      setPassword('');
      setError('');
      setVerifying(false);
    }
  }, [isOpen]);

  if (!isOpen || !ruleInfo) return null;

  const handleNext = async () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (confirmText !== 'CONFIRM') {
        setError('Please type CONFIRM exactly');
        return;
      }
      setError('');
      setStep(3);
    } else if (step === 3) {
      if (!password) {
        setError('Please enter your password');
        return;
      }
      setVerifying(true);
      setError('');
      try {
        const token = localStorage.getItem('admin_auth_token');
        const response = await fetch(`${API_BASE}/bom/rules/verify-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ password }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ message: 'Invalid password' }));
          throw new Error(err.message || 'Invalid password');
        }
        onConfirm();
      } catch (err: any) {
        setError(err.message || 'Password verification failed');
      } finally {
        setVerifying(false);
      }
    }
  };

  const getActionLabel = () => {
    switch (ruleInfo.action) {
      case 'create': return 'create a new';
      case 'update': return 'edit a';
      case 'delete': return 'delete a';
      default: return 'modify a';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full">
          {/* Header */}
          <div className="bg-amber-500 px-6 py-4 rounded-t-xl">
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h2 className="text-xl font-bold text-white">Edit Protection</h2>
                <p className="text-amber-100 text-sm">Step {step} of 3</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 1 && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">
                    You are about to {getActionLabel()} manufacturing rule that affects all production calculations.
                  </p>
                </div>
                <div className="text-sm text-gray-600 space-y-2">
                  <p><strong>Rule Type:</strong> {ruleInfo.ruleType}</p>
                  <p><strong>Rule Name:</strong> {ruleInfo.ruleName}</p>
                </div>
                <p className="text-sm text-gray-500">
                  Changes to manufacturing rules can impact:
                </p>
                <ul className="text-sm text-gray-500 list-disc list-inside space-y-1">
                  <li>Cut sheet calculations for all orders</li>
                  <li>BOM generation and inventory allocation</li>
                  <li>Production workflow and quality control</li>
                </ul>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  To confirm you understand the impact, please type <strong className="text-red-600">CONFIRM</strong> below:
                </p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="Type CONFIRM"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-center text-lg font-mono"
                  autoFocus
                />
                {error && (
                  <p className="text-red-600 text-sm">{error}</p>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  Enter your admin password to unlock editing:
                </p>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleNext()}
                />
                {error && (
                  <p className="text-red-600 text-sm">{error}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              disabled={verifying || (step === 2 && confirmText !== 'CONFIRM')}
              className={clsx(
                'px-6 py-2 rounded-lg font-medium transition-colors',
                step === 3
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-500 text-white hover:bg-amber-600',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {verifying ? 'Verifying...' : step === 3 ? 'Unlock & Edit' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// No Permission Banner
function NoEditPermissionBanner() {
  return (
    <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded-lg flex items-center gap-3">
      <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      <div>
        <p className="font-medium text-gray-700">View-Only Mode</p>
        <p className="text-sm text-gray-500">You do not have permission to edit manufacturing rules. Contact an administrator if you need edit access.</p>
      </div>
    </div>
  );
}

// Types
interface CutFormula {
  id: string;
  componentName: string;
  componentType: string;
  cutFormula: string;
  uChannelAffects: boolean;
  description?: string;
  notes?: string;
}

interface FabricSeamRule {
  id: string;
  fabricType: string;
  fabricCode: string;
  rollWidth: number;
  maxPanelCut: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ManufacturingRule {
  id: string;
  ruleType: string;
  name: string;
  description?: string;
  conditionField: string;
  conditionOperator: string;
  conditionValue: string;
  actionType: string;
  actionValue: number | null;
  severity: string;
  executionOrder: number;
  active: boolean;
  appliesTo?: string;
  createdAt: string;
  updatedAt: string;
}

interface SpringConfig {
  id?: string;
  name?: string;
  topHoleOffset: number;
  bottomHoleOffset: number;
  minSpacing: number;
  maxSpacing: number;
  tightSpacingThreshold: number;
}

// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

// Tab types
type TabType = 'cuts' | 'installation' | 'options' | 'seams' | 'validation' | 'spring';

// Modal Component
function Modal({ isOpen, onClose, title, children }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Component Cuts Tab
function ComponentCutsTab() {
  const { canEdit, requestEdit } = useEditProtection();
  const [formulas, setFormulas] = useState<CutFormula[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<CutFormula>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadFormulas = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/bom/rules/cut-formulas');
      setFormulas(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load cut formulas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFormulas();
  }, []);

  const handleEdit = (formula: CutFormula) => {
    if (!canEdit) return;
    setEditingId(formula.id);
    setEditValues({
      cutFormula: formula.cutFormula,
      uChannelAffects: formula.uChannelAffects,
      notes: formula.notes || '',
    });
  };

  const handleSave = async (id: string, formula: CutFormula) => {
    if (!canEdit) return;

    const doSave = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest(`/bom/rules/cut-formulas/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(editValues),
        });
        setSuccess('Formula updated successfully');
        setEditingId(null);
        loadFormulas();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to save formula');
      } finally {
        setSaving(false);
      }
    };

    requestEdit(doSave, {
      ruleType: 'Cut Formula',
      ruleName: formula.componentName,
      ruleId: id,
      fieldChanged: 'cutFormula',
      oldValue: formula.cutFormula,
      newValue: editValues.cutFormula || '',
      action: 'update',
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Component Cut Formulas</h3>
        <p className="text-sm text-gray-500">
          Define formulas for calculating cut sizes for each component. Use variables like <code className="bg-gray-100 px-1 rounded">W</code> (width) and <code className="bg-gray-100 px-1 rounded">H</code> (height).
        </p>
      </div>

      {/* Factory Deductions Info Banner */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
        <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="text-sm text-blue-800">
          <strong>Note:</strong> Deductions are only applied to cuts when the order has "Apply Factory Deductions" enabled.
          Selecting U-Channel alone does not trigger deductions — the dealer must check the deductions checkbox.
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm border-b">
              <th className="pb-3 font-medium">Component</th>
              <th className="pb-3 font-medium">Type</th>
              <th className="pb-3 font-medium">Cut Formula <InfoTooltip text="Cut calculation using variables: W_system = system width (already includes u-channel deduction), H_system = opening height, boxW = box width (includes ¼ inch and dealer adj, no u-channel), u_channel_ded = raw u-channel deduction (0, 0.375, or 0.75)." /></th>
              <th className="pb-3 font-medium">
                <span className="flex items-center gap-1">
                  Deduction Applies
                  <InfoTooltip text="When checked, this component's cut is affected by u-channel deduction (already included in W_system). Only applies when 'Apply Factory Deductions' is enabled on order." />
                </span>
              </th>
              <th className="pb-3 font-medium">Notes</th>
              <th className="pb-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {formulas.map((f) => (
              <tr key={f.id} className="border-b hover:bg-gray-50">
                <td className="py-3">
                  <span className="font-medium">{f.componentName}</span>
                  {f.description && (
                    <p className="text-xs text-gray-500 mt-1">{f.description}</p>
                  )}
                </td>
                <td className="py-3 capitalize">{f.componentType}</td>
                <td className="py-3">
                  {editingId === f.id ? (
                    <input
                      type="text"
                      value={editValues.cutFormula || ''}
                      onChange={(e) => setEditValues({ ...editValues, cutFormula: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                    />
                  ) : (
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">{f.cutFormula}</code>
                  )}
                </td>
                <td className="py-3">
                  {editingId === f.id ? (
                    <input
                      type="checkbox"
                      checked={editValues.uChannelAffects || false}
                      onChange={(e) => setEditValues({ ...editValues, uChannelAffects: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600"
                      title="Check if this formula includes the deduction variable"
                    />
                  ) : (
                    <span
                      className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium cursor-help',
                        f.uChannelAffects ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      )}
                      title={f.uChannelAffects
                        ? 'This formula includes the deduction variable. Deduction only applies when enabled on the order.'
                        : 'This formula does not include factory deductions.'
                      }
                    >
                      {f.uChannelAffects ? 'Yes' : 'No'}
                    </span>
                  )}
                </td>
                <td className="py-3">
                  {editingId === f.id ? (
                    <input
                      type="text"
                      value={editValues.notes || ''}
                      onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Optional notes..."
                    />
                  ) : (
                    <span className="text-sm text-gray-500">{f.notes || '-'}</span>
                  )}
                </td>
                <td className="py-3">
                  {editingId === f.id ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave(f.id, f)}
                        disabled={saving}
                        className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : canEdit ? (
                    <button
                      onClick={() => handleEdit(f)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  ) : (
                    <span className="text-gray-400 text-sm">View only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Installation Adjustments Tab
function InstallationAdjustmentsTab() {
  const { canEdit, requestEdit } = useEditProtection();
  const [rules, setRules] = useState<ManufacturingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<ManufacturingRule>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    conditionField: 'track_mount',
    conditionOperator: '=',
    conditionValue: 'face',
    actionType: 'add',
    actionValue: 6,
    severity: 'auto',
    appliesTo: 'track',
  });

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/bom/rules/manufacturing?type=installation');
      setRules(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load installation rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleEdit = (rule: ManufacturingRule) => {
    if (!canEdit) return;
    setEditingId(rule.id);
    setEditValues({
      name: rule.name,
      description: rule.description,
      actionValue: rule.actionValue,
      active: rule.active,
    });
  };

  const handleSave = async (id: string, rule: ManufacturingRule) => {
    if (!canEdit) return;

    const doSave = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest(`/bom/rules/manufacturing/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(editValues),
        });
        setSuccess('Rule updated successfully');
        setEditingId(null);
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to save rule');
      } finally {
        setSaving(false);
      }
    };

    requestEdit(doSave, {
      ruleType: 'Installation Adjustment',
      ruleName: rule.name,
      ruleId: id,
      fieldChanged: 'actionValue',
      oldValue: String(rule.actionValue),
      newValue: String(editValues.actionValue),
      action: 'update',
    });
  };

  const handleCreate = async () => {
    if (!canEdit) return;

    const doCreate = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest('/bom/rules/manufacturing', {
          method: 'POST',
          body: JSON.stringify({
            ruleType: 'installation',
            ...newRule,
          }),
        });
        setSuccess('Rule created successfully');
        setShowModal(false);
        setNewRule({
          name: '',
          description: '',
          conditionField: 'track_mount',
          conditionOperator: '=',
          conditionValue: 'face',
          actionType: 'add',
          actionValue: 6,
          severity: 'auto',
          appliesTo: 'track',
        });
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to create rule');
      } finally {
        setSaving(false);
      }
    };

    requestEdit(doCreate, {
      ruleType: 'Installation Adjustment',
      ruleName: newRule.name,
      oldValue: '',
      newValue: JSON.stringify(newRule),
      action: 'create',
    });
  };

  const handleDelete = async (id: string, rule: ManufacturingRule) => {
    if (!canEdit) return;

    const doDelete = async () => {
      try {
        await apiRequest(`/bom/rules/manufacturing/${id}`, { method: 'DELETE' });
        setSuccess('Rule deleted');
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to delete rule');
      }
    };

    requestEdit(doDelete, {
      ruleType: 'Installation Adjustment',
      ruleName: rule.name,
      ruleId: id,
      oldValue: JSON.stringify(rule),
      newValue: '',
      action: 'delete',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Installation Adjustments</h3>
          <p className="text-sm text-gray-500">
            Track mount adjustments — face/wall mounts add 3" per side (+6" total); inside &amp; jamb add nothing.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Rule
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Quick Reference */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Standard Track Mount Adjustments (applied automatically)</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li><strong>Face / Wall Mount:</strong> +3" per side → <strong>+6" total</strong> (track overlaps the opening)</li>
          <li><strong>Inside Mount:</strong> no adjustment (track fits inside the opening)</li>
          <li><strong>Jamb Mount:</strong> no adjustment</li>
        </ul>
        <p className="text-xs text-blue-700 mt-2">
          Enforced by the BOM engine from each unit's track mount — a face/wall mount grows the box &amp; system width by 6".
          This is the authoritative behavior; the custom rules below are reference/notes only and do not override it.
        </p>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>Track-mount adjustments are applied automatically by the BOM engine (see the reference above).</p>
          <p className="text-sm mt-1">No custom reference rules are configured — the engine does not require any to be added here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-500 text-sm border-b">
                <th className="pb-3 font-medium">Rule Name</th>
                <th className="pb-3 font-medium">Condition</th>
                <th className="pb-3 font-medium">Adjustment</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b hover:bg-gray-50">
                  <td className="py-3">
                    {editingId === r.id ? (
                      <input
                        type="text"
                        value={editValues.name || ''}
                        onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    ) : (
                      <div>
                        <span className="font-medium">{r.name}</span>
                        {r.description && (
                          <p className="text-xs text-gray-500 mt-1">{r.description}</p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {r.conditionField} {r.conditionOperator} {r.conditionValue}
                    </code>
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex items-center gap-1">
                        <span className="text-sm">{r.actionType === 'add' ? '+' : '-'}</span>
                        <input
                          type="number"
                          value={editValues.actionValue || 0}
                          onChange={(e) => setEditValues({ ...editValues, actionValue: parseFloat(e.target.value) })}
                          onWheel={(e) => e.currentTarget.blur()}
                          step="0.125"
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <span className="text-sm">"</span>
                      </div>
                    ) : (
                      <span className={clsx(
                        'font-medium',
                        r.actionType === 'add' ? 'text-green-600' : 'text-red-600'
                      )}>
                        {r.actionType === 'add' ? '+' : '-'}{r.actionValue}"
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <select
                        value={editValues.active ? 'active' : 'inactive'}
                        onChange={(e) => setEditValues({ ...editValues, active: e.target.value === 'active' })}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    ) : (
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      )}>
                        {r.active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(r.id, r)}
                          disabled={saving}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : canEdit ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(r)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(r.id, r)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Installation Rule">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
            <input
              type="text"
              value={newRule.name}
              onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              placeholder='e.g., Face Mount +6"'
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newRule.description}
              onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field</label>
              <select
                value={newRule.conditionField}
                onChange={(e) => setNewRule({ ...newRule, conditionField: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="track_mount">Track Mount</option>
                <option value="box_mount">Box Mount</option>
                <option value="recessed">Recessed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
              <select
                value={newRule.conditionOperator}
                onChange={(e) => setNewRule({ ...newRule, conditionOperator: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="=">=</option>
                <option value="!=">!=</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
              <select
                value={newRule.conditionValue}
                onChange={(e) => setNewRule({ ...newRule, conditionValue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="face">Face</option>
                <option value="inside">Inside</option>
                <option value="jamb">Jamb</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={newRule.actionType}
                onChange={(e) => setNewRule({ ...newRule, actionType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="add">Add (+)</option>
                <option value="subtract">Subtract (-)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value (inches)</label>
              <input
                type="number"
                value={newRule.actionValue}
                onChange={(e) => setNewRule({ ...newRule, actionValue: parseFloat(e.target.value) })}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.125"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || !newRule.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Rule'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Option Adjustments Tab (U-Channel Deductions)
function OptionAdjustmentsTab() {
  const { canEdit, requestEdit } = useEditProtection();
  const [rules, setRules] = useState<ManufacturingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<ManufacturingRule>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    conditionField: 'u_channel',
    conditionOperator: '=',
    conditionValue: 'true',
    actionType: 'subtract',
    actionValue: 0.375,
    severity: 'auto',
    appliesTo: 'width',
  });

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/bom/rules/manufacturing?type=option');
      setRules(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load option rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleEdit = (rule: ManufacturingRule) => {
    if (!canEdit) return;
    setEditingId(rule.id);
    setEditValues({
      name: rule.name,
      description: rule.description,
      actionValue: rule.actionValue,
      active: rule.active,
    });
  };

  const handleSave = async (id: string, rule: ManufacturingRule) => {
    if (!canEdit) return;
    const doSave = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest(`/bom/rules/manufacturing/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(editValues),
        });
        setSuccess('Rule updated successfully');
        setEditingId(null);
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to save rule');
      } finally {
        setSaving(false);
      }
    };
    requestEdit(doSave, {
      ruleType: 'Option Adjustment',
      ruleName: rule.name,
      ruleId: id,
      oldValue: String(rule.actionValue),
      newValue: String(editValues.actionValue),
      action: 'update',
    });
  };

  const handleCreate = async () => {
    if (!canEdit) return;
    const doCreate = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest('/bom/rules/manufacturing', {
          method: 'POST',
          body: JSON.stringify({
            ruleType: 'option',
            ...newRule,
          }),
        });
        setSuccess('Rule created successfully');
        setShowModal(false);
        setNewRule({
          name: '',
          description: '',
          conditionField: 'u_channel',
          conditionOperator: '=',
          conditionValue: 'true',
          actionType: 'subtract',
          actionValue: 0.375,
          severity: 'auto',
          appliesTo: 'width',
        });
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to create rule');
      } finally {
        setSaving(false);
      }
    };
    requestEdit(doCreate, {
      ruleType: 'Option Adjustment',
      ruleName: newRule.name,
      oldValue: '',
      newValue: JSON.stringify(newRule),
      action: 'create',
    });
  };

  const handleDelete = async (id: string, rule: ManufacturingRule) => {
    if (!canEdit) return;
    const doDelete = async () => {
      try {
        await apiRequest(`/bom/rules/manufacturing/${id}`, { method: 'DELETE' });
        setSuccess('Rule deleted');
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to delete rule');
      }
    };
    requestEdit(doDelete, {
      ruleType: 'Option Adjustment',
      ruleName: rule.name,
      ruleId: id,
      oldValue: JSON.stringify(rule),
      newValue: '',
      action: 'delete',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Option Adjustments</h3>
          <p className="text-sm text-gray-500">
            U-channel and other option-based deductions (e.g., -0.375" per side).
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Rule
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Quick Reference */}
      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <h4 className="font-medium text-amber-900 mb-2">Standard U-Channel Deductions</h4>
        <ul className="text-sm text-amber-800 space-y-1">
          <li>U-Channel Left: -0.375" (3/8") from width</li>
          <li>U-Channel Right: -0.375" (3/8") from width</li>
          <li>U-Channel Both Sides: -0.75" total from width</li>
        </ul>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No option adjustment rules configured.</p>
          <p className="text-sm mt-1">Add rules for U-channel and other option-based deductions.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-500 text-sm border-b">
                <th className="pb-3 font-medium">Rule Name</th>
                <th className="pb-3 font-medium">Condition</th>
                <th className="pb-3 font-medium">Applies To</th>
                <th className="pb-3 font-medium">Deduction</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b hover:bg-gray-50">
                  <td className="py-3">
                    {editingId === r.id ? (
                      <input
                        type="text"
                        value={editValues.name || ''}
                        onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    ) : (
                      <div>
                        <span className="font-medium">{r.name}</span>
                        {r.description && (
                          <p className="text-xs text-gray-500 mt-1">{r.description}</p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {r.conditionField} {r.conditionOperator} {r.conditionValue}
                    </code>
                  </td>
                  <td className="py-3 capitalize">{r.appliesTo || 'width'}</td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex items-center gap-1">
                        <span className="text-sm">-</span>
                        <input
                          type="number"
                          value={editValues.actionValue || 0}
                          onChange={(e) => setEditValues({ ...editValues, actionValue: parseFloat(e.target.value) })}
                          onWheel={(e) => e.currentTarget.blur()}
                          step="0.001"
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <span className="text-sm">"</span>
                      </div>
                    ) : (
                      <span className="font-medium text-red-600">
                        -{r.actionValue}"
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <select
                        value={editValues.active ? 'active' : 'inactive'}
                        onChange={(e) => setEditValues({ ...editValues, active: e.target.value === 'active' })}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    ) : (
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      )}>
                        {r.active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(r.id, r)}
                          disabled={saving}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : canEdit ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(r)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(r.id, r)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Option Adjustment Rule">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
            <input
              type="text"
              value={newRule.name}
              onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              placeholder="e.g., U-Channel Left Deduction"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newRule.description}
              onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field</label>
              <select
                value={newRule.conditionField}
                onChange={(e) => setNewRule({ ...newRule, conditionField: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="u_channel">U-Channel</option>
                <option value="u_channel_left">U-Channel Left</option>
                <option value="u_channel_right">U-Channel Right</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
              <select
                value={newRule.conditionOperator}
                onChange={(e) => setNewRule({ ...newRule, conditionOperator: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="=">=</option>
                <option value="!=">!=</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
              <select
                value={newRule.conditionValue}
                onChange={(e) => setNewRule({ ...newRule, conditionValue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
              <select
                value={newRule.appliesTo}
                onChange={(e) => setNewRule({ ...newRule, appliesTo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="width">Width</option>
                <option value="height">Height</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Deduction (inches)</label>
              <input
                type="number"
                value={newRule.actionValue}
                onChange={(e) => setNewRule({ ...newRule, actionValue: parseFloat(e.target.value) })}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || !newRule.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Rule'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Seam Rules Tab
function SeamRulesTab() {
  const { canEdit, requestEdit } = useEditProtection();
  const [rules, setRules] = useState<FabricSeamRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<FabricSeamRule>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newRule, setNewRule] = useState({
    fabricType: 'solar',
    fabricCode: '',
    rollWidth: 118,
    maxPanelCut: 116,
  });

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/bom/rules/fabric-seam');
      setRules(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load seam rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleEdit = (rule: FabricSeamRule) => {
    if (!canEdit) return;
    setEditingId(rule.id);
    setEditValues({
      rollWidth: rule.rollWidth,
      maxPanelCut: rule.maxPanelCut,
      active: rule.active,
    });
  };

  const handleSave = async (id: string, rule: FabricSeamRule) => {
    if (!canEdit) return;
    const doSave = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest(`/bom/rules/fabric-seam/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(editValues),
        });
        setSuccess('Rule updated successfully');
        setEditingId(null);
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to save rule');
      } finally {
        setSaving(false);
      }
    };
    requestEdit(doSave, {
      ruleType: 'Fabric Seam Rule',
      ruleName: `${rule.fabricType} - ${rule.fabricCode || 'Default'}`,
      ruleId: id,
      oldValue: `Roll: ${rule.rollWidth}", Max: ${rule.maxPanelCut}"`,
      newValue: `Roll: ${editValues.rollWidth}", Max: ${editValues.maxPanelCut}"`,
      action: 'update',
    });
  };

  const handleCreate = async () => {
    if (!canEdit) return;
    const doCreate = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest('/bom/rules/fabric-seam', {
          method: 'POST',
          body: JSON.stringify(newRule),
        });
        setSuccess('Rule created successfully');
        setShowModal(false);
        setNewRule({
          fabricType: 'solar',
          fabricCode: '',
          rollWidth: 118,
          maxPanelCut: 116,
        });
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to create rule');
      } finally {
        setSaving(false);
      }
    };
    requestEdit(doCreate, {
      ruleType: 'Fabric Seam Rule',
      ruleName: `${newRule.fabricType} - ${newRule.fabricCode || 'Default'}`,
      oldValue: '',
      newValue: JSON.stringify(newRule),
      action: 'create',
    });
  };

  const handleDelete = async (id: string, rule: FabricSeamRule) => {
    if (!canEdit) return;
    const doDelete = async () => {
      try {
        await apiRequest(`/bom/rules/fabric-seam/${id}`, { method: 'DELETE' });
        setSuccess('Rule deleted');
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to delete rule');
      }
    };
    requestEdit(doDelete, {
      ruleType: 'Fabric Seam Rule',
      ruleName: `${rule.fabricType} - ${rule.fabricCode || 'Default'}`,
      ruleId: id,
      oldValue: JSON.stringify(rule),
      newValue: '',
      action: 'delete',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Fabric Seam Rules</h3>
          <p className="text-sm text-gray-500">
            Define when fabric seams are required based on roll width and panel dimensions.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Rule
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Info Box */}
      <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <h4 className="font-medium text-purple-900 mb-2">Seam Calculation</h4>
        <p className="text-sm text-purple-800">
          If panel height exceeds the <strong>Max Panel Cut</strong> for a fabric type, a seam will be required.
          The system will flag these orders for production review.
        </p>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No seam rules configured.</p>
          <p className="text-sm mt-1">Add rules to define fabric roll widths and seam requirements.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-500 text-sm border-b">
                <th className="pb-3 font-medium">Fabric Type</th>
                <th className="pb-3 font-medium">Fabric Code</th>
                <th className="pb-3 font-medium">Roll Width</th>
                <th className="pb-3 font-medium">Max Panel Cut</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 capitalize font-medium">{r.fabricType}</td>
                  <td className="py-3">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">{r.fabricCode || '-'}</code>
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={editValues.rollWidth || 0}
                          onChange={(e) => setEditValues({ ...editValues, rollWidth: parseFloat(e.target.value) })}
                          onWheel={(e) => e.currentTarget.blur()}
                          step="0.5"
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <span className="text-sm">"</span>
                      </div>
                    ) : (
                      <span>{r.rollWidth}"</span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={editValues.maxPanelCut || 0}
                          onChange={(e) => setEditValues({ ...editValues, maxPanelCut: parseFloat(e.target.value) })}
                          onWheel={(e) => e.currentTarget.blur()}
                          step="0.5"
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <span className="text-sm">"</span>
                      </div>
                    ) : (
                      <span className="font-medium text-blue-600">{r.maxPanelCut}"</span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <select
                        value={editValues.active ? 'active' : 'inactive'}
                        onChange={(e) => setEditValues({ ...editValues, active: e.target.value === 'active' })}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    ) : (
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      )}>
                        {r.active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(r.id, r)}
                          disabled={saving}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : canEdit ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(r)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(r.id, r)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Fabric Seam Rule">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fabric Type</label>
              <select
                value={newRule.fabricType}
                onChange={(e) => setNewRule({ ...newRule, fabricType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="solar">Solar</option>
                <option value="premium_solar">Premium Solar</option>
                <option value="cvw">Clear Vinyl (CVW)</option>
                <option value="insect">Insect</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fabric Code (optional)</label>
              <input
                type="text"
                value={newRule.fabricCode}
                onChange={(e) => setNewRule({ ...newRule, fabricCode: e.target.value })}
                placeholder="e.g., S94, S99"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Roll Width (inches)</label>
              <input
                type="number"
                value={newRule.rollWidth}
                onChange={(e) => setNewRule({ ...newRule, rollWidth: parseFloat(e.target.value) })}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Panel Cut (inches)</label>
              <input
                type="number"
                value={newRule.maxPanelCut}
                onChange={(e) => setNewRule({ ...newRule, maxPanelCut: parseFloat(e.target.value) })}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Panels exceeding this height will require a seam</p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Rule'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Validation Rules Tab
function ValidationRulesTab() {
  const { canEdit, requestEdit } = useEditProtection();
  const [rules, setRules] = useState<ManufacturingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<ManufacturingRule>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    conditionField: 'width',
    conditionOperator: '>',
    conditionValue: '240',
    actionType: 'reject',
    actionValue: 0,
    severity: 'error',
    appliesTo: 'all',
  });

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/bom/rules/manufacturing?type=validation');
      setRules(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load validation rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleEdit = (rule: ManufacturingRule) => {
    if (!canEdit) return;
    setEditingId(rule.id);
    setEditValues({
      name: rule.name,
      description: rule.description,
      conditionValue: rule.conditionValue,
      severity: rule.severity,
      active: rule.active,
    });
  };

  const handleSave = async (id: string, rule: ManufacturingRule) => {
    if (!canEdit) return;
    const doSave = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest(`/bom/rules/manufacturing/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(editValues),
        });
        setSuccess('Rule updated successfully');
        setEditingId(null);
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to save rule');
      } finally {
        setSaving(false);
      }
    };
    requestEdit(doSave, {
      ruleType: 'Validation Rule',
      ruleName: rule.name,
      ruleId: id,
      oldValue: rule.conditionValue,
      newValue: editValues.conditionValue || '',
      action: 'update',
    });
  };

  const handleCreate = async () => {
    if (!canEdit) return;
    const doCreate = async () => {
      setSaving(true);
      setError('');
      try {
        await apiRequest('/bom/rules/manufacturing', {
          method: 'POST',
          body: JSON.stringify({
            ruleType: 'validation',
            ...newRule,
          }),
        });
        setSuccess('Rule created successfully');
        setShowModal(false);
        setNewRule({
          name: '',
          description: '',
          conditionField: 'width',
          conditionOperator: '>',
          conditionValue: '240',
          actionType: 'reject',
          actionValue: 0,
          severity: 'error',
          appliesTo: 'all',
        });
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to create rule');
      } finally {
        setSaving(false);
      }
    };
    requestEdit(doCreate, {
      ruleType: 'Validation Rule',
      ruleName: newRule.name,
      oldValue: '',
      newValue: JSON.stringify(newRule),
      action: 'create',
    });
  };

  const handleDelete = async (id: string, rule: ManufacturingRule) => {
    if (!canEdit) return;
    const doDelete = async () => {
      try {
        await apiRequest(`/bom/rules/manufacturing/${id}`, { method: 'DELETE' });
        setSuccess('Rule deleted');
        loadRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to delete rule');
      }
    };
    requestEdit(doDelete, {
      ruleType: 'Validation Rule',
      ruleName: rule.name,
      ruleId: id,
      oldValue: JSON.stringify(rule),
      newValue: '',
      action: 'delete',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Validation Rules</h3>
          <p className="text-sm text-gray-500">
            Max/min dimension rules and other validation constraints.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Rule
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Quick Reference */}
      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <h4 className="font-medium text-red-900 mb-2">Standard Validation Rules</h4>
        <ul className="text-sm text-red-800 space-y-1">
          <li>Maximum Width: 240" (20 feet)</li>
          <li>Maximum Height: 192" (16 feet)</li>
          <li>Minimum Width: 24"</li>
          <li>Minimum Height: 24"</li>
        </ul>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No validation rules configured.</p>
          <p className="text-sm mt-1">Add rules to enforce dimension limits and constraints.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-500 text-sm border-b">
                <th className="pb-3 font-medium">Rule Name</th>
                <th className="pb-3 font-medium">Condition</th>
                <th className="pb-3 font-medium">Severity</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b hover:bg-gray-50">
                  <td className="py-3">
                    {editingId === r.id ? (
                      <input
                        type="text"
                        value={editValues.name || ''}
                        onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    ) : (
                      <div>
                        <span className="font-medium">{r.name}</span>
                        {r.description && (
                          <p className="text-xs text-gray-500 mt-1">{r.description}</p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex items-center gap-1">
                        <span className="text-sm">{r.conditionField} {r.conditionOperator}</span>
                        <input
                          type="text"
                          value={editValues.conditionValue || ''}
                          onChange={(e) => setEditValues({ ...editValues, conditionValue: e.target.value })}
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <span className="text-sm">"</span>
                      </div>
                    ) : (
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {r.conditionField} {r.conditionOperator} {r.conditionValue}"
                      </code>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <select
                        value={editValues.severity || 'error'}
                        onChange={(e) => setEditValues({ ...editValues, severity: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="error">Error (Reject)</option>
                        <option value="warning">Warning</option>
                        <option value="info">Info</option>
                      </select>
                    ) : (
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        r.severity === 'error' ? 'bg-red-100 text-red-700' :
                        r.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      )}>
                        {r.severity}
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <select
                        value={editValues.active ? 'active' : 'inactive'}
                        onChange={(e) => setEditValues({ ...editValues, active: e.target.value === 'active' })}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    ) : (
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      )}>
                        {r.active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    {editingId === r.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(r.id, r)}
                          disabled={saving}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : canEdit ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(r)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(r.id, r)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Validation Rule">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
            <input
              type="text"
              value={newRule.name}
              onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              placeholder="e.g., Maximum Width 240 inches"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newRule.description}
              onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field</label>
              <select
                value={newRule.conditionField}
                onChange={(e) => setNewRule({ ...newRule, conditionField: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="width">Width</option>
                <option value="height">Height</option>
                <option value="area">Area</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
              <select
                value={newRule.conditionOperator}
                onChange={(e) => setNewRule({ ...newRule, conditionOperator: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value=">">&gt;</option>
                <option value="<">&lt;</option>
                <option value=">=">&gt;=</option>
                <option value="<=">&lt;=</option>
                <option value="=">=</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value (inches)</label>
              <input
                type="text"
                value={newRule.conditionValue}
                onChange={(e) => setNewRule({ ...newRule, conditionValue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <select
              value={newRule.severity}
              onChange={(e) => setNewRule({ ...newRule, severity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="error">Error (Reject order)</option>
              <option value="warning">Warning (Allow but flag)</option>
              <option value="info">Info (Display message only)</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || !newRule.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Rule'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Spring Formula Tab
function SpringFormulaTab() {
  const { canEdit, requestEdit } = useEditProtection();
  const [config, setConfig] = useState<SpringConfig>({
    topHoleOffset: 8,
    bottomHoleOffset: 12,
    minSpacing: 16,
    maxSpacing: 20,
    tightSpacingThreshold: 144,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState<SpringConfig>(config);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/bom/rules/spring-config');
      setConfig(data);
      setEditValues(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load spring configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleStartEdit = () => {
    if (!canEdit) return;
    setEditing(true);
  };

  const handleSave = async () => {
    if (!canEdit) return;

    const doSave = async () => {
      setSaving(true);
      setError('');
      try {
        const updated = await apiRequest('/bom/rules/spring-config', {
          method: 'PATCH',
          body: JSON.stringify(editValues),
        });
        setConfig(updated);
        setSuccess('Spring configuration updated successfully');
        setEditing(false);
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to save configuration');
      } finally {
        setSaving(false);
      }
    };

    requestEdit(doSave, {
      ruleType: 'Spring Configuration',
      ruleName: 'Spring Hole Formula',
      oldValue: JSON.stringify(config),
      newValue: JSON.stringify(editValues),
      action: 'update',
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setEditValues(config);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Spring Hole Configuration</h3>
          <p className="text-sm text-gray-500">
            Configure spring hole positioning formula for different panel heights.
          </p>
        </div>
        {!editing && canEdit && (
          <button
            onClick={handleStartEdit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Edit Configuration
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Visual Diagram */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Spring Hole Placement Diagram</h4>
        <div className="flex justify-center">
          <div className="relative w-24 h-64 border-2 border-gray-400 rounded bg-white">
            {/* Top offset */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 text-xs text-center">
              <div className="h-8 border-l border-dashed border-blue-500"></div>
              <span className="text-blue-600">{editing ? editValues.topHoleOffset : config.topHoleOffset}"</span>
            </div>

            {/* Top hole */}
            <div className="absolute top-10 left-1/2 transform -translate-x-1/2 w-3 h-3 rounded-full bg-blue-500"></div>

            {/* Middle spacing indicator */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xs text-center">
              <span className="text-gray-600">{editing ? editValues.minSpacing : config.minSpacing}-{editing ? editValues.maxSpacing : config.maxSpacing}"</span>
              <br />
              <span className="text-gray-400">spacing</span>
            </div>

            {/* Bottom hole */}
            <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-3 h-3 rounded-full bg-blue-500"></div>

            {/* Bottom offset */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-xs text-center">
              <span className="text-blue-600">{editing ? editValues.bottomHoleOffset : config.bottomHoleOffset}"</span>
              <div className="h-8 border-l border-dashed border-blue-500"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Hole Offsets</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Top Hole Offset (from top) <InfoTooltip text="Distance from top of track to first spring hole. Default: 8 inches." />
            </label>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={editValues.topHoleOffset}
                  onChange={(e) => setEditValues({ ...editValues, topHoleOffset: parseFloat(e.target.value) })}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.5"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">inches</span>
              </div>
            ) : (
              <p className="text-lg font-semibold text-gray-900">{config.topHoleOffset}"</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Distance from top of panel to first spring hole</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bottom Hole Offset (from bottom) <InfoTooltip text="Distance from bottom of track to last spring hole. Default: 12 inches." />
            </label>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={editValues.bottomHoleOffset}
                  onChange={(e) => setEditValues({ ...editValues, bottomHoleOffset: parseFloat(e.target.value) })}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.5"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">inches</span>
              </div>
            ) : (
              <p className="text-lg font-semibold text-gray-900">{config.bottomHoleOffset}"</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Distance from bottom of panel to last spring hole</p>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Spacing Rules</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Spacing <InfoTooltip text="Minimum distance between spring holes. Default: 16 inches." />
            </label>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={editValues.minSpacing}
                  onChange={(e) => setEditValues({ ...editValues, minSpacing: parseFloat(e.target.value) })}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.5"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">inches</span>
              </div>
            ) : (
              <p className="text-lg font-semibold text-gray-900">{config.minSpacing}"</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Minimum distance between spring holes</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maximum Spacing <InfoTooltip text="Maximum distance between spring holes. Default: 20 inches." />
            </label>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={editValues.maxSpacing}
                  onChange={(e) => setEditValues({ ...editValues, maxSpacing: parseFloat(e.target.value) })}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.5"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">inches</span>
              </div>
            ) : (
              <p className="text-lg font-semibold text-gray-900">{config.maxSpacing}"</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Maximum distance between spring holes</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tight Spacing Threshold
            </label>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={editValues.tightSpacingThreshold}
                  onChange={(e) => setEditValues({ ...editValues, tightSpacingThreshold: parseFloat(e.target.value) })}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="1"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">inches</span>
              </div>
            ) : (
              <p className="text-lg font-semibold text-gray-900">{config.tightSpacingThreshold}"</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Height threshold for using minimum spacing</p>
          </div>
        </div>
      </div>

      {editing && (
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      )}

      {/* Algorithm Explanation */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Spring Hole Calculation Algorithm</h4>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Calculate usable height: Panel Height - Top Offset - Bottom Offset</li>
          <li>Determine spacing based on height threshold</li>
          <li>Calculate number of holes needed</li>
          <li>Distribute holes evenly within usable area</li>
          <li>Round positions to nearest 1/8" increment</li>
        </ol>
      </div>
    </div>
  );
}

// Main Page Component
export function ManufacturingRulesPage() {
  const [activeTab, setActiveTab] = useState<TabType>('cuts');
  const [canEdit, setCanEdit] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    callback: () => Promise<void>;
    ruleInfo: { ruleType: string; ruleName: string; ruleId?: string; fieldChanged?: string; oldValue: string; newValue: string; action: string };
  } | null>(null);

  // Check edit permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const token = localStorage.getItem('admin_auth_token');
        const response = await fetch(`${API_BASE}/bom/rules/edit-permission`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await response.json();
        setCanEdit(data.canEdit || false);
      } catch (err) {
        console.error('Failed to check edit permission:', err);
        setCanEdit(false);
      } finally {
        setPermissionLoading(false);
      }
    };
    checkPermission();
  }, []);

  // Request edit with multi-step confirmation
  const requestEdit = useCallback((
    callback: () => Promise<void>,
    ruleInfo: { ruleType: string; ruleName: string; ruleId?: string; fieldChanged?: string; oldValue: string; newValue: string; action: string }
  ) => {
    setPendingAction({ callback, ruleInfo });
    setShowConfirmModal(true);
  }, []);

  // Handle confirmed edit
  const handleConfirmedEdit = async () => {
    if (!pendingAction) return;

    setShowConfirmModal(false);
    setIsUnlocked(true);

    try {
      // Execute the action
      await pendingAction.callback();

      // Log to audit
      const token = localStorage.getItem('admin_auth_token');
      await fetch(`${API_BASE}/bom/rules/audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(pendingAction.ruleInfo),
      });
    } catch (err) {
      console.error('Edit action failed:', err);
    } finally {
      // Lock again after single edit
      setTimeout(() => setIsUnlocked(false), 100);
      setPendingAction(null);
    }
  };

  const editProtectionValue = {
    canEdit,
    isUnlocked,
    requestEdit,
  };

  const tabs: { key: TabType; label: string; icon: React.ReactNode }[] = [
    {
      key: 'cuts',
      label: 'Component Cuts',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
        </svg>
      ),
    },
    {
      key: 'installation',
      label: 'Installation',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      key: 'options',
      label: 'Options',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
    },
    {
      key: 'seams',
      label: 'Seam Rules',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
    },
    {
      key: 'validation',
      label: 'Validation',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'spring',
      label: 'Spring Formula',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];

  if (permissionLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <EditProtectionContext.Provider value={editProtectionValue}>
      <PageHeader
        title="Manufacturing Rules"
        subtitle="Configure cut formulas, dimension adjustments, and validation rules"
      />

      {/* Edit Permission Banner */}
      {!canEdit && <NoEditPermissionBanner />}

      {/* Edit Status Indicator */}
      {canEdit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm text-amber-800">
            <strong>Protected Mode:</strong> Each edit requires confirmation and password verification. All changes are logged.
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
              activeTab === tab.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Card>
        {activeTab === 'cuts' && <ComponentCutsTab />}
        {activeTab === 'installation' && <InstallationAdjustmentsTab />}
        {activeTab === 'options' && <OptionAdjustmentsTab />}
        {activeTab === 'seams' && <SeamRulesTab />}
        {activeTab === 'validation' && <ValidationRulesTab />}
        {activeTab === 'spring' && <SpringFormulaTab />}
      </Card>

      {/* Edit Confirmation Modal */}
      <EditConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingAction(null);
        }}
        onConfirm={handleConfirmedEdit}
        ruleInfo={pendingAction?.ruleInfo || null}
      />
    </EditProtectionContext.Provider>
  );
}

export default ManufacturingRulesPage;
